import { Link } from "react-router-dom";
import type { Receipt } from "@/api/types";
import { Modal } from "./Modal";
import { ReceiptStatusBadge } from "./ReceiptStatusBadge";
import {
  attentionReason,
  attentionReasonLabel,
} from "@/utils/receiptReadiness";
import { receiptTitle } from "@/utils/receipt";
import { pluralReceipts } from "@/utils/format";
import "./close-readiness-modal.css";

export interface CloseReadinessModalProps {
  open: boolean;
  tripSlug: string;
  /** The needs-attention receipts that block the close (ADR-0029 §2). */
  blocking: ReadonlyArray<Receipt>;
  /** Whether every blocking receipt is still processing (softer copy). */
  transientOnly: boolean;
  /** «Спершу завершити» → route to the filtered receipts tab. */
  onResolve: () => void;
  /** «Скасувати» → dismiss; the trip stays Open. */
  onCancel: () => void;
}

/**
 * ConfirmCloseTrip as a HARD GATE (components.md §5.1, user-flows §US-03).
 * When ≥1 receipt needs attention, close is BLOCKED — there is no «Все одно
 * закрити». The visitor resolves the receipts (finish / retry / set payer /
 * set currency / delete) or cancels. Reuses <Modal>'s dialog semantics; the
 * primary confirm becomes «Спершу завершити» which routes to the filtered
 * list rather than firing a doomed close.
 */
export function CloseReadinessModal({
  open,
  tripSlug,
  blocking,
  transientOnly,
  onResolve,
  onCancel,
}: CloseReadinessModalProps) {
  const count = blocking.length;
  const title = transientOnly
    ? "Зачекай, чек ще обробляється"
    : `${pluralReceipts(count)} ще треба завершити перед закриттям`;

  return (
    <Modal
      open={open}
      title={title}
      confirmLabel="Спершу завершити"
      cancelLabel="Скасувати"
      onConfirm={onResolve}
      onCancel={onCancel}
    >
      <ol className="wp-close-gate" data-testid="close-gate-checklist">
        {blocking.map((r) => {
          const reason = attentionReason(r);
          return (
            <li key={r.receipt_id} className="wp-close-gate__row">
              <Link
                to={`/t/${tripSlug}/receipts/${r.receipt_id}`}
                className="wp-close-gate__link"
                onClick={onCancel}
              >
                <ReceiptStatusBadge status={r.status} />
                <span className="wp-close-gate__title">{receiptTitle(r)}</span>
                {reason ? (
                  <span className="wp-close-gate__reason">
                    {attentionReasonLabel(reason)}
                  </span>
                ) : null}
                <span aria-hidden="true" className="wp-close-gate__chevron">
                  →
                </span>
              </Link>
            </li>
          );
        })}
      </ol>
      <p className="wp-close-gate__foot">
        Поки ці чеки не завершені, поїздку не можна закрити.
      </p>
    </Modal>
  );
}
